/**
 * Shopify host bridge for the iframed Bloks Konfigurator.
 * - Iframe URL + share address-bar sync
 * - Ajax cart add (replace only matching configuration lines)
 * - Checkout handoff navigates to /cart (does not open the fly-in drawer)
 * - Buchstützen upsell in the host drawer ↔ SPA state
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import {
  buildMultiConfigCartAttributes,
  collectConfigsFromCartItems,
  getLineProperty,
  LINE_PROP_CONFIG_ID,
} from './bloks-konfigurator-cart-attributes.js';

const MSG_PREFIX = 'bloks-konfigurator:';

/** @type {Window | null} */
let spaWindow = null;
/** @type {string | null} */
let spaOrigin = null;

/** @type {{ count: number, unitPrice: number, max: number, currency?: string }} */
let buchstuetzenState = {
  count: 0,
  unitPrice: 0,
  max: 99,
};

function cartUpdateUrl() {
  const base = Theme?.routes?.cart_update_url || '/cart/update';
  return base.endsWith('.js') ? base : `${base}.js`;
}

function cartAddUrl() {
  return Theme?.routes?.cart_add_url || '/cart/add.js';
}

function cartJsUrl() {
  return `${Theme?.routes?.cart_url || '/cart'}.js`;
}

function getItemConfigId(item) {
  return getLineProperty(item?.properties, LINE_PROP_CONFIG_ID);
}

function getConfigIdFromPayload(items, attributes) {
  for (const item of items || []) {
    const id = getItemConfigId(item);
    if (id) return id;
  }
  return String(attributes?.['Konfigurations-ID'] || attributes?.Konfigurations_ID || '').trim();
}

/** Remove only lines that belong to this configuration — never the whole cart. */
async function removeConfigLines(configId) {
  if (!configId) return;

  const cartResponse = await fetch(cartJsUrl());
  const cart = await cartResponse.json();
  const updates = {};

  for (const item of cart.items || []) {
    if (getItemConfigId(item) === configId) {
      updates[item.key] = 0;
    }
  }

  if (Object.keys(updates).length === 0) return;

  await fetch(cartUpdateUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ updates }),
  });
}

/**
 * Rebuild Weitere Details from line properties after cart mutations.
 * @param {object} cart
 * @param {string} [ensureQtyConfigId] keep/set qty helper for this id
 */
async function syncCartConfigAttributes(cart, ensureQtyConfigId = '') {
  const configs = collectConfigsFromCartItems(cart?.items || []);
  const attributes = buildMultiConfigCartAttributes(
    configs,
    cart?.attributes || {},
  );
  if (ensureQtyConfigId && configs.some((c) => c.id === ensureQtyConfigId)) {
    const qtyKey = `_bloks_cfg_qty_${ensureQtyConfigId}`;
    if (!attributes[qtyKey]) {
      attributes[qtyKey] = String(cart?.attributes?.[qtyKey] || '1');
    }
  }

  const updateResponse = await fetch(cartUpdateUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ attributes }),
  });

  if (!updateResponse.ok) {
    console.warn(
      '[BloksKonfiguratorHost] cart attributes update failed',
      updateResponse.status,
    );
  }

  return attributes;
}

function elevateCartDrawerStacking() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  const dialog = drawer?.querySelector('dialog');
  if (!drawer || !dialog) return;

  drawer.classList.add('bloks-konfig-cart-drawer');
  dialog.classList.add('bloks-konfig-cart-drawer__dialog');
  dialog.style.setProperty('z-index', '2147483000', 'important');
}

/** Match SPA checkout wash: delay before “Weiter zum Warenkorb …” on success. */
const CHECKOUT_CONTINUE_HINT_MS = 900;
const CHECKOUT_MESSAGE_OUT_MS = 720;

/** @type {ReturnType<typeof setTimeout> | null} */
let checkoutContinueHintTimer = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let checkoutMessageCleanupTimer = null;

function defaultCheckoutContinueHint() {
  const lang = String(document.documentElement.lang || 'de').toLowerCase();
  return lang.startsWith('en')
    ? 'Continuing to cart …'
    : 'Weiter zum Warenkorb …';
}

function ensureCheckoutWashEl() {
  let el = document.getElementById('bloks-konfig-checkout-wash');
  if (el) return el;

  el = document.createElement('div');
  el.id = 'bloks-konfig-checkout-wash';
  el.className = 'bloks-konfig-checkout-wash';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = `
    <div class="bloks-konfig-checkout-wash__content">
      <div class="bloks-konfig-checkout-wash__spinner">
        <div class="bloks-konfig-checkout-wash__symbol" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect class="seg seg-top" width="32" height="5"></rect>
            <rect class="seg seg-bottom" y="27" width="32" height="5"></rect>
            <rect class="seg seg-left" x="2" y="5" width="1" height="22"></rect>
            <rect class="seg seg-right" x="29" y="5" width="1" height="22"></rect>
          </svg>
        </div>
      </div>
      <div class="bloks-konfig-checkout-wash__message-slot"></div>
    </div>
    <p class="bloks-konfig-checkout-wash__continue" hidden></p>
  `;
  document.body.appendChild(el);
  return el;
}

function resetCheckoutWashMessages(slot) {
  if (checkoutMessageCleanupTimer) {
    clearTimeout(checkoutMessageCleanupTimer);
    checkoutMessageCleanupTimer = null;
  }
  if (slot) slot.replaceChildren();
}

function setCheckoutWashMessage(slot, message) {
  if (!slot || typeof message !== 'string' || !message) return;

  const incoming = slot.querySelector('.bloks-konfig-checkout-wash__message--in');
  if (incoming?.textContent === message) return;

  if (incoming) {
    incoming.classList.remove('bloks-konfig-checkout-wash__message--in');
    incoming.classList.add('bloks-konfig-checkout-wash__message--out');
    if (checkoutMessageCleanupTimer) clearTimeout(checkoutMessageCleanupTimer);
    checkoutMessageCleanupTimer = setTimeout(() => {
      incoming.remove();
      checkoutMessageCleanupTimer = null;
    }, CHECKOUT_MESSAGE_OUT_MS);
  }

  const next = document.createElement('p');
  next.className =
    'bloks-konfig-checkout-wash__message bloks-konfig-checkout-wash__message--in';
  next.textContent = message;
  slot.appendChild(next);
}

function setCheckoutContinueHint(continueEl, { success, continueMessage }) {
  if (checkoutContinueHintTimer) {
    clearTimeout(checkoutContinueHintTimer);
    checkoutContinueHintTimer = null;
  }

  if (!continueEl) return;

  if (!success) {
    continueEl.hidden = true;
    continueEl.classList.remove('bloks-konfig-checkout-wash__continue--visible');
    continueEl.textContent = '';
    return;
  }

  continueEl.hidden = true;
  continueEl.classList.remove('bloks-konfig-checkout-wash__continue--visible');
  continueEl.textContent = '';

  checkoutContinueHintTimer = setTimeout(() => {
    continueEl.textContent =
      (typeof continueMessage === 'string' && continueMessage) ||
      defaultCheckoutContinueHint();
    continueEl.hidden = false;
    // Force a frame so the fade-in animation always plays.
    void continueEl.offsetWidth;
    continueEl.classList.add('bloks-konfig-checkout-wash__continue--visible');
    checkoutContinueHintTimer = null;
  }, CHECKOUT_CONTINUE_HINT_MS);
}

/**
 * Full-page wash matching the SPA checkout transition (covers chrome around the iframe).
 * @param {{ visible?: boolean, message?: string, success?: boolean, continueMessage?: string }} [options]
 */
function setHostCheckoutWash({
  visible = true,
  message = '',
  success = false,
  continueMessage = '',
} = {}) {
  const el = ensureCheckoutWashEl();
  const spinner = el.querySelector('.bloks-konfig-checkout-wash__spinner');
  const slot = el.querySelector('.bloks-konfig-checkout-wash__message-slot');
  const continueEl = el.querySelector('.bloks-konfig-checkout-wash__continue');

  el.classList.toggle('bloks-konfig-checkout-wash--success', Boolean(success));
  spinner?.classList.toggle(
    'bloks-konfig-checkout-wash__spinner--hidden',
    Boolean(success),
  );

  if (visible) {
    setCheckoutWashMessage(slot, message);
    setCheckoutContinueHint(continueEl, { success, continueMessage });
    el.setAttribute('aria-hidden', 'false');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    // Force a frame so the opacity transition always plays.
    void el.offsetWidth;
    el.classList.add('bloks-konfig-checkout-wash--visible');
  } else {
    el.classList.remove(
      'bloks-konfig-checkout-wash--visible',
      'bloks-konfig-checkout-wash--success',
    );
    spinner?.classList.remove('bloks-konfig-checkout-wash__spinner--hidden');
    resetCheckoutWashMessages(slot);
    setCheckoutContinueHint(continueEl, { success: false, continueMessage: '' });
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('role');
  }
}

async function openKonfiguratorCartDrawer() {
  elevateCartDrawerStacking();
  showBuchstuetzenUpsell(true);

  const drawer = document.querySelector('theme-drawer#cart-drawer');
  try {
    if (typeof drawer?.open === 'function') {
      await drawer.open();
      return;
    }
  } catch (error) {
    console.warn('[BloksKonfiguratorHost] theme-drawer.open failed', error);
  }

  try {
    await window.Shopify?.actions?.openCart?.();
  } catch (error) {
    console.warn('[BloksKonfiguratorHost] openCart failed', error);
  }
}

/**
 * Theme cart-drawer auto-opens on CartLinesUpdateEvent action "add".
 * Strip that briefly so konfigurator checkout can navigate to /cart instead.
 * @returns {() => void} restore
 */
function suppressCartDrawerAutoOpen() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!cartDrawer?.hasAttribute('auto-open')) return () => {};

  cartDrawer.removeAttribute('auto-open');
  return () => {
    cartDrawer.setAttribute('auto-open', '');
  };
}

function closeKonfiguratorCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  try {
    if (typeof drawer?.close === 'function') {
      drawer.close();
    }
  } catch (error) {
    console.warn('[BloksKonfiguratorHost] theme-drawer.close failed', error);
  }
}

/**
 * @param {{ id: number, quantity: number }[]} items
 * @param {Record<string, string>} [attributes]
 * @param {{ openDrawer?: boolean, replace?: boolean }} [options]
 */
async function addItems(items, attributes = {}, options = {}) {
  const openDrawer = options.openDrawer === true;
  const replace = options.replace === true;

  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Keine Artikel zum Hinzufügen.');
  }

  if (replace) {
    await removeConfigLines(getConfigIdFromPayload(items, attributes));
  }

  // Must run before dispatching CartLinesUpdateEvent — cart-drawer.js reads
  // auto-open synchronously and would otherwise fly the drawer in mid-wash.
  const restoreAutoOpen = openDrawer ? () => {} : suppressCartDrawerAutoOpen();

  try {
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    const addResponse = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    const addData = await addResponse.json();
    if (addData.status) {
      const message = addData.description || addData.message || 'Add to cart failed';
      deferredEventPromise.reject(new Error(message));
      throw new Error(message);
    }

    const cartAfterAddResponse = await fetch(cartJsUrl());
    const cartAfterAdd = await cartAfterAddResponse.json();
    const addedConfigId = getConfigIdFromPayload(items, attributes);
    await syncCartConfigAttributes(cartAfterAdd, addedConfigId);

    const cartResponse = await fetch(cartJsUrl());
    const cart = await cartResponse.json();

    deferredEventPromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bloks-konfigurator',
        didError: false,
      },
    });

    if (openDrawer) {
      // Let Horizon morph the drawer contents, then bring it above the iframe.
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      await openKonfiguratorCartDrawer();
    } else {
      closeKonfiguratorCartDrawer();
    }

    return cart;
  } finally {
    restoreAutoOpen();
  }
}

window.BloksKonfiguratorCart = {
  addItems: (items, attributes) => addItems(items, attributes, { openDrawer: false }),
};

function allowedAssetOrigin(assetBaseUrl) {
  try {
    return new URL(assetBaseUrl).origin;
  } catch {
    return null;
  }
}

function buildIframeSrc(boot) {
  const assetBase = String(boot.assetBaseUrl || '').replace(/\/$/, '');
  if (!assetBase) return null;

  const params = new URLSearchParams(window.location.search);
  params.delete('preview_theme_id');
  params.set('embedded', '1');
  params.set('cartMode', boot.cartMode || 'ajax');
  params.set('parentOrigin', window.location.origin);
  if (boot.publicAppUrl) params.set('publicAppUrl', boot.publicAppUrl);
  if (boot.apiBaseUrl) params.set('apiBaseUrl', boot.apiBaseUrl);
  if (boot.assetBaseUrl) params.set('assetBaseUrl', boot.assetBaseUrl);
  if (boot.locale) params.set('locale', boot.locale);

  return `${assetBase}/?${params.toString()}`;
}

function syncHostAddressBar({ shareId, clearShareId }) {
  const url = new URL(window.location.href);
  if (clearShareId) {
    url.searchParams.delete('id');
  } else if (shareId) {
    url.searchParams.set('id', String(shareId));
  } else {
    return;
  }
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
}

function formatEuro(amount) {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

function showBuchstuetzenUpsell(visible) {
  const root = document.getElementById('bloks-konfigurator-cart-upsell');
  if (!root) return;
  root.hidden = !visible;
  if (visible) syncBuchstuetzenUpsellUi();
}

function syncBuchstuetzenUpsellUi() {
  const root = document.getElementById('bloks-konfigurator-cart-upsell');
  if (!root) return;

  const input = root.querySelector('[data-bloks-buchstuetzen-input]');
  const priceEl = root.querySelector('[data-bloks-buchstuetzen-price]');
  const minus = root.querySelector('[data-bloks-buchstuetzen-delta="-1"]');
  const plus = root.querySelector('[data-bloks-buchstuetzen-delta="1"]');

  if (input && document.activeElement !== input) {
    input.value = String(buchstuetzenState.count);
  }
  if (priceEl) {
    const total = buchstuetzenState.count * buchstuetzenState.unitPrice;
    priceEl.textContent =
      buchstuetzenState.count > 0 ? formatEuro(total) : formatEuro(buchstuetzenState.unitPrice) + ' / Stk.';
  }
  if (minus) minus.disabled = buchstuetzenState.count <= 0;
  if (plus) plus.disabled = buchstuetzenState.count >= buchstuetzenState.max;
}

function postToSpa(payload) {
  if (!spaWindow || !spaOrigin) return;
  spaWindow.postMessage(
    { source: 'bloks-konfigurator-host', ...payload },
    spaOrigin
  );
}

function requestBuchstuetzenCount(nextCount) {
  const clamped = Math.max(0, Math.min(buchstuetzenState.max, Math.floor(Number(nextCount) || 0)));
  buchstuetzenState = { ...buchstuetzenState, count: clamped };
  syncBuchstuetzenUpsellUi();
  postToSpa({
    type: `${MSG_PREFIX}buchstuetzen-set`,
    count: clamped,
  });
}

function bindBuchstuetzenUpsellControls() {
  const root = document.getElementById('bloks-konfigurator-cart-upsell');
  if (!root || root.dataset.bound === 'true') return;
  root.dataset.bound = 'true';

  root.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-bloks-buchstuetzen-delta]');
    if (!btn) return;
    const delta = Number(btn.getAttribute('data-bloks-buchstuetzen-delta'));
    if (!Number.isFinite(delta)) return;
    requestBuchstuetzenCount(buchstuetzenState.count + delta);
  });

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-bloks-buchstuetzen-input]');
    if (!input) return;
    requestBuchstuetzenCount(input.value);
  });

  root.addEventListener('keydown', (event) => {
    const input = event.target.closest('[data-bloks-buchstuetzen-input]');
    if (!input || event.key !== 'Enter') return;
    event.preventDefault();
    requestBuchstuetzenCount(input.value);
    input.blur();
  });
}

function mountIframe() {
  const bootEl = document.getElementById('bloks-konfigurator-boot');
  const frame = document.getElementById('bloks-konfigurator-frame');
  const root = document.getElementById('bloks-konfigurator-root');
  if (!bootEl || !frame) return;

  let boot;
  try {
    boot = JSON.parse(bootEl.textContent);
  } catch (error) {
    console.error('[BloksKonfiguratorHost] Invalid boot JSON', error);
    return;
  }

  window.__BLOKS_KONFIGURATOR_BOOT__ = boot;
  const src = buildIframeSrc(boot);
  if (!src) {
    if (root) {
      root.innerHTML =
        '<div class="bloks-konfigurator-root__error" role="alert">Konfigurator assetBaseUrl fehlt.</div>';
    }
    return;
  }

  const assetOrigin = allowedAssetOrigin(boot.assetBaseUrl);
  spaOrigin = assetOrigin;
  frame.src = src;
  bindBuchstuetzenUpsellControls();
  elevateCartDrawerStacking();

  frame.addEventListener('load', () => {
    spaWindow = frame.contentWindow;
  });

  window.addEventListener('message', async (event) => {
    if (assetOrigin && event.origin !== assetOrigin) return;
    const data = event.data;
    if (!data || data.source !== 'bloks-konfigurator') return;

    spaWindow = event.source;
    spaOrigin = event.origin;

    if (data.type === `${MSG_PREFIX}url-sync`) {
      syncHostAddressBar(data);
      return;
    }

    if (data.type === `${MSG_PREFIX}buchstuetzen-state`) {
      buchstuetzenState = {
        count: Math.max(0, Math.floor(Number(data.count) || 0)),
        unitPrice: Number(data.unitPrice) || 0,
        max: Math.max(0, Math.floor(Number(data.max) || 99)),
      };
      showBuchstuetzenUpsell(true);
      syncBuchstuetzenUpsellUi();
      return;
    }

    if (data.type === `${MSG_PREFIX}checkout-wash`) {
      if (data.visible !== false) {
        // Keep the fly-in cart closed while the wash / cart-page handoff runs.
        closeKonfiguratorCartDrawer();
      }
      setHostCheckoutWash({
        visible: data.visible !== false,
        message: data.message || '',
        success: Boolean(data.success),
        continueMessage: data.continueMessage || '',
      });
      return;
    }

    if (
      data.type === `${MSG_PREFIX}cart-go` ||
      data.type === `${MSG_PREFIX}cart-open`
    ) {
      closeKonfiguratorCartDrawer();
      // Prefer SPA-provided locale path (e.g. /en/cart); fall back for older builds.
      const path = data.cartPath || data.href || '/cart';
      window.location.assign(path);
      return;
    }

    if (data.type === `${MSG_PREFIX}cart-replace`) {
      try {
        await addItems(data.items || [], data.attributes || {}, {
          openDrawer: false,
          replace: true,
        });
        event.source?.postMessage(
          {
            source: 'bloks-konfigurator-host',
            type: `${MSG_PREFIX}cart-replace-result`,
            requestId: data.requestId,
            ok: true,
          },
          event.origin
        );
      } catch (error) {
        event.source?.postMessage(
          {
            source: 'bloks-konfigurator-host',
            type: `${MSG_PREFIX}cart-replace-result`,
            requestId: data.requestId,
            ok: false,
            error: error?.message || 'Warenkorb-Update fehlgeschlagen.',
          },
          event.origin
        );
      }
      return;
    }

    if (data.type !== `${MSG_PREFIX}cart-add`) return;

    const reply = (payload) => {
      event.source?.postMessage(
        {
          source: 'bloks-konfigurator-host',
          type: `${MSG_PREFIX}cart-add-result`,
          requestId: data.requestId,
          ...payload,
        },
        event.origin
      );
    };

    try {
      // Do not open the drawer — SPA handoff navigates to /cart after the wash.
      const cart = await addItems(data.items, data.attributes || {}, {
        openDrawer: false,
        replace: Boolean(data.replace),
      });
      reply({ ok: true, cart });
    } catch (error) {
      reply({ ok: false, error: error?.message || 'Warenkorb fehlgeschlagen.' });
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountIframe, { once: true });
} else {
  mountIframe();
}
